import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PenLine, Clapperboard, Check, Palette, Loader2, Sparkles, Save, RefreshCw } from "lucide-react";
import { mistApi, pipelinesApi, streamAsset } from "@/api/mist";
import { GenerateBar, ResultCard, AssetPicker } from "@/components/mist/shared";
import UniversalSaveModal, { emergencyCommit } from "@/components/mist/UniversalSaveModal";
import { RenderProgress } from "@/components/mist/RenderProgress";
import { useStudioParam } from "@/hooks/useStudioUrl";
import { consumeDirectorPrompt } from "@/lib/directorPrefill";

const NAV = [
  { k: "create", label: "Create Poem", Icon: PenLine },
  { k: "video", label: "Add to Video", Icon: Clapperboard },
];

/* ------------------------------ CREATE POEM ----------------------------- */
function CreatePoem({ initialPrompt = "", onPoem }) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await mistApi.invoke({ macro_tab: "writing", sub_tab_action: "poetry_maker", prompt_text: prompt.trim() });
      setResult(r);
      if (r?.content) onPoem?.(r.content);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col" data-testid="poem-create-view">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-2">
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Creative prompt</span>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} data-testid="poem-input"
            placeholder="e.g. the quiet hum of ambition at 3am"
            className="mt-1 w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-[#2a2a2a] rounded-xl px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A] resize-none" />
        </label>
        <ResultCard result={result} />
      </div>
      <GenerateBar onRun={run} busy={busy} label="Write" />
    </div>
  );
}

/* ------------------------------ ADD TO VIDEO ---------------------------- */
const POEM_MODES = [
  { k: "static", label: "Static Overlay", sub: "Crisp text over an image", track: "image" },
  { k: "scroll", label: "Movie Credit Scroll", sub: "Text scrolls up over a video", track: "video" },
];

function AddToVideo({ poemText }) {
  const [mode, setMode] = useState("scroll");
  const [sel, setSel] = useState([]);
  const [text, setText] = useState(poemText || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [prog, setProg] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const track = POEM_MODES.find((m) => m.k === mode).track;

  // Stage 5 · AI Backdrop
  const [bdPrompt, setBdPrompt] = useState("");
  const [bdBusy, setBdBusy] = useState(false);
  const [backdrop, setBackdrop] = useState(null);   // { filename, asset_id }
  const [bdSrc, setBdSrc] = useState(null);

  useEffect(() => () => { if (bdSrc) URL.revokeObjectURL(bdSrc); }, [bdSrc]);

  const genBackdrop = async () => {
    if (!bdPrompt.trim()) { toast.error("Describe the artwork background"); return; }
    setBdBusy(true);
    try {
      const { job_id } = await pipelinesApi.poemBackdrop(bdPrompt.trim());
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id && j.filename) {
        setBackdrop({ filename: j.filename, asset_id: j.asset_id });
        const url = await streamAsset(j.asset_id);
        if (bdSrc) URL.revokeObjectURL(bdSrc);
        setBdSrc(url);
        setSel([]);   // generated backdrop supersedes a picked asset
        toast.success(`Backdrop ready · ${j.engine}`);
      } else { toast.error(j.error || "Backdrop generation failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Backdrop failed"); }
    finally { setBdBusy(false); }
  };

  // In static mode a generated backdrop can stand in for a picked asset.
  const bgFile = (mode === "static" && backdrop) ? backdrop.filename : sel[0];

  const run = async () => {
    if (!bgFile) { toast.error(mode === "static" ? "Generate or pick a background" : `Pick a background ${track}`); return; }
    if (!text.trim()) { toast.error("Add poem text to overlay"); return; }
    setBusy(true); setResult(null); setSaved(false); setProg(0);
    try {
      const { job_id } = await pipelinesApi.poemVideo(bgFile, text.trim(), mode);
      const j = await pipelinesApi.poll(job_id, { onTick: (t) => setProg(typeof t.progress === "number" ? t.progress : null) });
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: "video" }, engine: j.engine, filename: `poem_${Date.now()}.mp4` });
        // MD §2 Law 3 — a generated backdrop is intermediate once the final burns.
        if (backdrop?.asset_id) { try { await mistApi.discardAsset(backdrop.asset_id); } catch { /* best-effort */ } }
        if (j.engine === "raw_fallback" || j.engine === "poem_sim") toast("Exported raw background — burn-in limited in preview", { icon: "🎬" });
        else toast.success("Poem burned onto background");
      } else { toast.error(j.error || "Overlay failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Overlay failed"); }
    finally { setBusy(false); setProg(null); }
  };

  const clearAndRestart = async () => {
    if (result?.asset?.id && !saved) { try { await mistApi.discardAsset(result.asset.id); } catch { /* ignore */ } }
    setResult(null); setSaved(false);
  };
  useEffect(() => {
    const id = result?.asset?.id;
    if (!id || saved) return undefined;
    const h = () => emergencyCommit(id);
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [result, saved]);

  return (
    <div className="h-full w-full flex flex-col" data-testid="poem-video-view">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-2 space-y-4">
        {/* Layout config toggles */}
        <div>
          <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Overlay style</span>
          <div className="grid grid-cols-2 gap-2" data-testid="poem-preset-switches">
            {POEM_MODES.map((m) => {
              const on = mode === m.k;
              return (
                <button key={m.k} onClick={() => { setMode(m.k); setSel([]); }} data-testid={`poem-mode-${m.k}`}
                  className={`flex flex-col items-start gap-1 border-2 border-[#2a2a2a] rounded-xl p-3 text-left shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${on ? "bg-gold" : "bg-[#141414]"}`}>
                  <span className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-wide text-white">
                    {on && <Check size={12} />} {m.label}
                  </span>
                  <span className="font-mono text-[8px] uppercase tracking-wide text-white/55">{m.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 🎨 GENERATE ARTWORK BACKGROUND (static mode) */}
        {mode === "static" && (
          <div className="border-[3px] border-[#00F5D4] bg-black p-3 shadow-[0_0_16px_rgba(0,245,212,0.35)] space-y-2.5" data-testid="poem-backdrop-block">
            <p className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.14em] text-[#00F5D4]"><Palette size={13} /> 🎨 Generate Artwork Background</p>
            <textarea value={bdPrompt} onChange={(e) => setBdPrompt(e.target.value.slice(0, 400))} rows={2} data-testid="poem-backdrop-prompt"
              placeholder="e.g. moody neon rain over a midnight city skyline"
              className="w-full bg-[#0d0d0d] border-2 border-[#00F5D4]/50 text-white px-3 py-2 font-mono text-[11px] outline-none focus:ring-2 focus:ring-[#00F5D4] resize-none break-words" />
            <button onClick={genBackdrop} disabled={bdBusy} data-testid="poem-backdrop-generate"
              className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-[#00F5D4] shadow-[0_0_14px_rgba(0,245,212,0.5)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              {bdBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {bdBusy ? "Painting backdrop…" : "Generate Backdrop"}
            </button>
            {backdrop && <p className="font-mono text-[8px] uppercase tracking-widest text-[#00F5D4]/70">Backdrop active — it sits behind your poem text below.</p>}
          </div>
        )}

        <AssetPicker track={track} selected={sel} onChange={(v) => { setSel(v); setBackdrop(null); if (bdSrc) { URL.revokeObjectURL(bdSrc); setBdSrc(null); } }} max={1} testid="poem-video-picker" />

        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Poem text to overlay</span>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="poem-overlay-text"
            placeholder="Paste your poem or type lines to burn onto the clip"
            className="mt-1 w-full bg-[#141414] text-white placeholder:text-white/40 border-2 border-[#2a2a2a] rounded-xl px-3 py-2 font-mono text-[11px] outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A] resize-none whitespace-pre-wrap" />
        </label>

        {/* Live CSS preview */}
        <div>
          <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Live preview</span>
          <div className="relative aspect-video w-full overflow-hidden border-4 border-black bg-charcoal shadow-[4px_4px_0px_#000]" data-testid="poem-live-preview">
            {mode === "static" && bdSrc ? (
              <img src={bdSrc} alt="backdrop" className="absolute inset-0 w-full h-full object-cover" data-testid="poem-backdrop-preview" />
            ) : (
              <div className="absolute inset-0 grid place-items-center opacity-30"><Clapperboard size={40} className="text-white" /></div>
            )}
            {mode === "scroll" ? (
              <div className="absolute inset-x-3 top-0 h-full overflow-hidden">
                <p className="poem-credit-scroll whitespace-pre-line text-center font-serif text-white text-sm leading-relaxed" data-testid="poem-scroll-text">
                  {text || "your poem scrolls up here…"}
                </p>
              </div>
            ) : (
              <div className="absolute inset-0 grid place-items-center px-4">
                <p className="whitespace-pre-line text-center font-serif text-white text-base leading-relaxed bg-black/50 px-3 py-2" data-testid="poem-static-text">
                  {text || "your poem sits centered here"}
                </p>
              </div>
            )}
          </div>
        </div>

        {busy && <div className="flex justify-center py-2" data-testid="poem-progress"><RenderProgress value={prog} label="Burning poem" /></div>}
        <ResultCard result={result} />
        {result?.asset?.id && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button data-testid="poem-save" onClick={() => setSaveOpen(true)} className="flex-1 flex items-center justify-center gap-2 bg-gold text-black font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><Save size={13} /> {saved ? "Saved ✓ · Save again" : "Save"}</button>
              <button data-testid="poem-clear-restart" onClick={clearAndRestart} className="flex-1 flex items-center justify-center gap-2 bg-charcoal text-gold font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-gold active:translate-x-[2px] active:translate-y-[2px]"><RefreshCw size={13} /> Clear & Restart</button>
            </div>
            {!saved && <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 text-center">Auto-saves to your Vault if you leave — or tap Save</p>}
            <UniversalSaveModal open={saveOpen} onClose={() => setSaveOpen(false)} assetId={result.asset.id}
              track="VIDEOS" isMusic={false} defaultName="Mist poem" onSaved={() => setSaved(true)} />
          </div>
        )}
      </div>
      <GenerateBar onRun={run} busy={busy} label={mode === "scroll" ? "Burn Credit Scroll" : "Burn Static Overlay"} />
    </div>
  );
}

/* --------------------------------- POEM --------------------------------- */
export default function MistPoem({ initialPrompt = "" }) {
  const [nav, setNav] = useStudioParam("view", "create");
  const [poemText, setPoemText] = useState("");
  const [seed] = useState(() => initialPrompt || consumeDirectorPrompt());

  return (
    <div className="h-full w-full flex flex-col" data-testid="mist-poem">
      {/* 2-segmented nav */}
      <div className="shrink-0 px-4 pt-3 pb-2">
        <div className="grid grid-cols-2 gap-1.5 bg-charcoal p-1 rounded-2xl border-2 border-[#2a2a2a]" data-testid="poem-nav">
          {NAV.map((n) => (
            <button key={n.k} onClick={() => setNav(n.k)} data-testid={`poem-nav-${n.k}`}
              className={`flex items-center justify-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.08em] rounded-xl py-2 transition-colors ${nav === n.k ? "bg-gold text-black" : "text-white/60"}`}>
              <n.Icon size={13} /> {n.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {nav === "create" && <CreatePoem initialPrompt={seed} onPoem={setPoemText} />}
        {nav === "video" && <AddToVideo poemText={poemText} />}
      </div>
    </div>
  );
}
